import { unitRule } from "./economy-rules.js";
import {
  addRational,
  ceilRational,
  formatMicro,
  microFromString,
  multiplyRational,
  rational,
  rationalFromDecimal,
} from "./exact-numbers.js";
import type {
  BorderPosition,
  BorderSupply,
  LogisticsState,
  TroopComposition,
} from "./logistics-types.js";
import { UNIT_IDS } from "./logistics-types.js";
import { exactRecordKeys as exactKeys } from "./record-validation.js";

export const EMPTY_TROOPS: TroopComposition = Object.freeze({
  "guard-robot": "0",
  "raider-robot": "0",
  "siege-gun": "0",
});

export function travelDeadline(nowMs: number, travelMultiplier: string, stepMs: number): number {
  if (!Number.isSafeInteger(nowMs) || nowMs < 0 || !Number.isSafeInteger(stepMs) || stepMs <= 0)
    throw new TypeError("Invalid travel clock");
  const duration = ceilRational(
    multiplyRational(rational(30_000n), rationalFromDecimal(travelMultiplier)),
  );
  if (duration <= 0n) throw new TypeError("Travel multiplier must be positive");
  const raw = BigInt(nowMs) + duration;
  const deadline = ((raw + BigInt(stepMs) - 1n) / BigInt(stepMs)) * BigInt(stepMs);
  if (deadline > BigInt(Number.MAX_SAFE_INTEGER))
    throw new TypeError("Travel deadline is too large");
  return Number(deadline);
}

export function supplyForPosition(
  troops: TroopComposition,
  totalSupply: string,
  allocationPercent: number,
): BorderSupply {
  if (!Number.isInteger(allocationPercent) || allocationPercent < 0 || allocationPercent > 100)
    throw new TypeError("Supply allocation must be a whole percentage from 0 to 100");
  const required = UNIT_IDS.reduce(
    (sum, id) =>
      addRational(
        sum,
        multiplyRational(rationalFromDecimal(troops[id]), rational(BigInt(unitRule(id).supply))),
      ),
    rational(0n),
  );
  const received = multiplyRational(
    rationalFromDecimal(totalSupply),
    rational(BigInt(allocationPercent), 100n),
  );
  const effective =
    required.numerator === 0n
      ? rational(0n)
      : received.numerator * required.denominator >= required.numerator * received.denominator
        ? rational(1n)
        : rational(
            received.numerator * required.denominator,
            received.denominator * required.numerator,
          );
  return Object.freeze({
    required: decimal(required),
    received: decimal(received),
    effectiveness: decimal(effective),
  });
}

export function addTroops(left: TroopComposition, right: TroopComposition): TroopComposition {
  return Object.freeze(
    Object.fromEntries(
      UNIT_IDS.map((id) => [
        id,
        formatMicro((microFromString(left[id]) + microFromString(right[id])) as never),
      ]),
    ),
  ) as TroopComposition;
}

export function subtractTroops(left: TroopComposition, right: TroopComposition): TroopComposition {
  return Object.freeze(
    Object.fromEntries(
      UNIT_IDS.map((id) => {
        const value = microFromString(left[id]) - microFromString(right[id]);
        if (value < 0n) throw new TypeError(`Insufficient ${id}`);
        return [id, formatMicro(value as never)];
      }),
    ),
  ) as TroopComposition;
}

export function normalizeTroops(value: Readonly<Record<string, unknown>>): TroopComposition {
  exactKeys(value, UNIT_IDS);
  return Object.freeze(
    Object.fromEntries(UNIT_IDS.map((id) => [id, troopQuantity(value[id], id)])),
  ) as TroopComposition;
}

export function troopQuantity(value: unknown, label: string): string {
  if (typeof value !== "string") throw new TypeError(`Invalid ${label}`);
  const parsed = microFromString(value);
  if (parsed < 0n) throw new TypeError(`Invalid ${label}`);
  return formatMicro(parsed);
}

export function requiredBorder(state: LogisticsState, id: string): BorderPosition {
  const position = state.borders[id];
  if (!position) throw new TypeError(`Unknown border: ${id}`);
  return position;
}

function decimal(value: ReturnType<typeof rational>): string {
  const scaled = (value.numerator * 1_000_000n) / value.denominator;
  return formatMicro(scaled as never);
}
